import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Button } from 'react-bootstrap'
import ReactPlaceholder from 'react-placeholder/lib'
import { useHistory } from 'react-router-dom'
import { useGet, useSaveUpdate } from '../../../hooks/process-data'
import { appUrl, doToast } from '../../../utils'
import { FormCustom, Input } from '../../../components/form'
import { useSelector } from 'react-redux'
import SweetAlert2 from 'react-sweetalert2'
import LoadingIcon from '../../../components/loading/LoadingIcon'
import { generalTicketSummary, generalPaidLimit } from '../../../utils/promotion2x1'

export default function ZoneGeneralForm({ idEventZone }) {
  const history = useHistory()

  const [swalProps, setSwalProps] = useState({ show: false })
  const [getRow, setGetRow] = useState({})
  const [ready, setReady] = useState(false)
  const [qtyGeneral, setQtyGeneral] = useState(1)

  const { id_customers_reservation_event_zone } = useSelector(
    state => state.sessioncustomer
  )

  const { rows, isProcessing, error, setReload } = useGet({
    url: `/event/zone/${idEventZone}/get/public`,
    rowId: idEventZone
  })

  useEffect(() => {
    if (error) {
      history.push(appUrl({ url: '/' }))
    } else if (!isProcessing) {
      if (!rows) {
        history.push(appUrl({ url: '/' }))
      } else {
        setGetRow(rows)
        setReady(true)
      }
    }
  }, [rows, error, isProcessing, history])

  const isSoldOut = useCallback(
    () => getRow && parseInt(getRow.sold_out, 10) === 1,
    [getRow]
  )

  const availableTickets = useMemo(
    () => (getRow?.event_zone_detail || []).filter(r => String(r.status_event_zone).toUpperCase() === 'PENDING').length,
    [getRow]
  )

  const has2x1 = Boolean(getRow?.has_2x1)
  const maxPaid = generalPaidLimit(availableTickets, has2x1)

  const noAvailability = useMemo(
    () => isSoldOut() || maxPaid === 0,
    [isSoldOut, maxPaid]
  )

  const dataSubmit = useMemo(
    () => ({
      p_id_event_zone: idEventZone,
      p_id_customers_reservation_event_zone: id_customers_reservation_event_zone,
      p_qty_general: qtyGeneral,
      p_expected_2x1: has2x1 ? 1 : 0
    }),
    [idEventZone, id_customers_reservation_event_zone, qtyGeneral, has2x1]
  )

  const { doSubmit, setDoSubmit } = useSaveUpdate({
    data: dataSubmit,
    url: '/event/zone/insert/detail',
    setError: _ => setReload(true),
    setReload: _ => {
      setReload(true)
      setReady(false)
      setQtyGeneral(1)
      setSwalProps({
        icon: 'success',
        title: 'Entradas reservadas',
        show: true,
        confirmButtonText: 'Ir al carrito',
        onConfirm: _ => {
          history.push(appUrl({ url: '/carrito' }))
        },
        didClose: _ => {
          setSwalProps({ show: false })
        }
      })
    }
  })

  const handleSubmit = _ => {
    if (!getRow || noAvailability) return

    const qty = parseInt(qtyGeneral, 10)

    if (Number.isNaN(qty) || qty <= 0) {
      doToast({
        response: 'warning',
        message: 'Indica cuántas entradas deseas reservar.',
        toast
      })
      return
    }

    if (qty > maxPaid) {
      // Validación sin mostrar stock exacto
      doToast({
        response: 'warning',
        message: 'La cantidad seleccionada no está disponible.',
        toast
      })
      return
    }

    setDoSubmit(true)
  }

  const handleQtyChange = e => {
    const value = e.target.value

    if (!value) {
      setQtyGeneral('')
      return
    }

    const num = parseInt(value, 10)
    if (Number.isNaN(num)) return

    const max = maxPaid || 1
    const clamped = Math.max(1, Math.min(num, max))
    setQtyGeneral(clamped)
  }

  const qtyNumber = parseInt(qtyGeneral, 10)
  const hasValidQty = !Number.isNaN(qtyNumber) && qtyNumber > 0 && qtyNumber <= maxPaid
  const selection = generalTicketSummary(getRow, qtyNumber)

  const hasDetails =
    getRow?.event_zone_detail && getRow.event_zone_detail.length > 0

  return (
    <FormCustom viewSubmit={false} onSubmit={handleSubmit}>
      <ReactPlaceholder ready={ready} showLoadingAnimation getRow={10}>
        {hasDetails && !noAvailability && (
          <div className="d-flex flex-column h-100">
            {/* Paso 1: cantidad */}
            <div className="mb-3">
              <Input
                text={has2x1 ? "Boletos pagados" : "Cantidad de entradas"}
                placeholder="Ejemplo: 1"
                type="number"
                step="1"
                min="1"
                max={maxPaid || undefined}
                classNameParent="mb-2"
                required
                invalid="El campo es obligatorio."
                value={qtyGeneral}
                onChange={handleQtyChange}
                size="lg"
                disabled={noAvailability}
              />
              <small className="text-muted">
                {has2x1 ? "2x1: agregamos automáticamente una entrada gratis por cada boleto pagado, sin cargo por servicio para la gratuita." : "La disponibilidad final se confirmará al completar tu compra."}
              </small>
            </div>

            {/* Paso 2: resumen */}
            <div className="mb-3">
              <div className="border rounded-3 bg-white p-3">
                {hasValidQty ? (
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-semibold">
                        {has2x1
                          ? `${selection.paid} ${selection.paid === 1 ? 'pagado' : 'pagados'} + ${selection.free} gratis = ${selection.tickets} entradas`
                          : `${qtyNumber} ${qtyNumber === 1 ? 'entrada general seleccionada' : 'entradas generales seleccionadas'}`}
                      </div>
                      <small className="text-muted">
                        Podrás ver el detalle completo en tu carrito antes de
                        confirmar el pago.
                      </small>
                    </div>
                    <i className="fas fa-ticket-alt fs-4 text-muted" />
                  </div>
                ) : (
                  <div className="text-muted small">
                    Ingresa la cantidad de entradas que deseas reservar para ver
                    el resumen.
                  </div>
                )}
              </div>
            </div>

            {/* Paso 3: confirmar */}
            <div className="mt-auto">
              <div className="d-flex justify-content-between align-items-center mb-2">
                {hasValidQty && (
                  <small className="text-muted">
                    Se añadirá(n){' '}
                    <strong>{selection.tickets}</strong>{' '}
                    {selection.tickets === 1 ? 'entrada' : 'entradas'} al carrito.
                  </small>
                )}
              </div>

              <Button
                size="lg"
                type="submit"
                className="w-100 d-flex justify-content-center align-items-center"
                disabled={!hasValidQty || noAvailability || !ready}
              >
                <i className="fas fa-shopping-cart me-2" />
                <span>Añadir entradas al carrito</span>
              </Button>
            </div>
          </div>
        )}

        {(!hasDetails || noAvailability) && (
          <div className="alert alert-danger mb-0">
            <i className="fas fa-exclamation-triangle me-2" />
            <span>Entradas agotadas para esta zona.</span>
          </div>
        )}
      </ReactPlaceholder>

      {doSubmit && <LoadingIcon />}

      <SweetAlert2 {...swalProps} />
    </FormCustom>
  )
}
